import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const NewCaseModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    caseNumber: '',
    clientName: '',
    clientContact: '',
    matterType: '',
    assignedAdvocate: '',
    courtJurisdiction: '',
    priority: 'Medium',
    description: '',
    caseValue: '',
    nextCourtDate: ''
  });

  const [errors, setErrors] = useState({});

  const matterTypeOptions = [
    'Commercial Litigation',
    'Criminal Defense',
    'Family Law',
    'Land & Property',
    'Corporate Law',
    'Constitutional Law',
    'Employment Law',
    'Tax Law'
  ];

  const advocateOptions = [
    'Sarah Mwangi',
    'Michael Ochieng',
    'Patricia Wanjiru',
    'David Mutua',
    'Grace Kimani',
    'James Otieno'
  ];

  const jurisdictionOptions = [
    'High Court - Nairobi',
    'High Court - Mombasa',
    'High Court - Nakuru',
    'High Court - Eldoret',
    'Magistrate Court - Nairobi',
    'Magistrate Court - Eldoret',
    'Court of Appeal - Nairobi'
  ];

  const priorityOptions = ['Low', 'Medium', 'High'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.caseNumber.trim()) {
      newErrors.caseNumber = 'Case number is required';
    }
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }
    if (!formData.clientContact.trim()) {
      newErrors.clientContact = 'Client contact is required';
    }
    if (!formData.matterType) {
      newErrors.matterType = 'Matter type is required';
    }
    if (!formData.assignedAdvocate) {
      newErrors.assignedAdvocate = 'Assigned advocate is required';
    }
    if (!formData.courtJurisdiction) {
      newErrors.courtJurisdiction = 'Court jurisdiction is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Case description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
      // Reset form
      setFormData({
        caseNumber: '',
        clientName: '',
        clientContact: '',
        matterType: '',
        assignedAdvocate: '',
        courtJurisdiction: '',
        priority: 'Medium',
        description: '',
        caseValue: '',
        nextCourtDate: ''
      });
      setErrors({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary">Create New Case</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Case Number */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Case Number *
              </label>
              <input
                type="text"
                value={formData.caseNumber}
                onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.caseNumber ? 'border-error' : 'border-border'
                }`}
                placeholder="e.g., HC/CC/001/2024"
              />
              {errors.caseNumber && (
                <p className="mt-1 text-sm text-error">{errors.caseNumber}</p>
              )}
            </div>

            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Client Name *
              </label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.clientName ? 'border-error' : 'border-border'
                }`}
                placeholder="Enter client name"
              />
              {errors.clientName && (
                <p className="mt-1 text-sm text-error">{errors.clientName}</p>
              )}
            </div>

            {/* Client Contact */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Client Contact *
              </label>
              <input
                type="text"
                value={formData.clientContact}
                onChange={(e) => handleInputChange('clientContact', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.clientContact ? 'border-error' : 'border-border'
                }`}
                placeholder="+254 712 345 678"
              />
              {errors.clientContact && (
                <p className="mt-1 text-sm text-error">{errors.clientContact}</p>
              )}
            </div>

            {/* Matter Type */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Matter Type *
              </label>
              <select
                value={formData.matterType}
                onChange={(e) => handleInputChange('matterType', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.matterType ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select matter type</option>
                {matterTypeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.matterType && (
                <p className="mt-1 text-sm text-error">{errors.matterType}</p>
              )}
            </div>

            {/* Assigned Advocate */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Assigned Advocate *
              </label>
              <select
                value={formData.assignedAdvocate}
                onChange={(e) => handleInputChange('assignedAdvocate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.assignedAdvocate ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select advocate</option>
                {advocateOptions.map(advocate => (
                  <option key={advocate} value={advocate}>{advocate}</option>
                ))}
              </select>
              {errors.assignedAdvocate && (
                <p className="mt-1 text-sm text-error">{errors.assignedAdvocate}</p>
              )}
            </div>

            {/* Court Jurisdiction */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Court Jurisdiction *
              </label>
              <select
                value={formData.courtJurisdiction}
                onChange={(e) => handleInputChange('courtJurisdiction', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.courtJurisdiction ? 'border-error' : 'border-border'
                }`}
              >
                <option value="">Select jurisdiction</option>
                {jurisdictionOptions.map(jurisdiction => (
                  <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                ))}
              </select>
              {errors.courtJurisdiction && (
                <p className="mt-1 text-sm text-error">{errors.courtJurisdiction}</p>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {priorityOptions.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            {/* Case Value */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Case Value (KES)
              </label>
              <input
                type="number"
                value={formData.caseValue}
                onChange={(e) => handleInputChange('caseValue', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Next Court Date */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Next Court Date
              </label>
              <input
                type="date"
                value={formData.nextCourtDate}
                onChange={(e) => handleInputChange('nextCourtDate', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Case Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.description ? 'border-error' : 'border-border'
              }`}
              placeholder="Provide a detailed description of the case..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-error">{errors.description}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-border text-text-primary rounded-lg hover:bg-secondary-50 transition-smooth"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
            >
              Create Case
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCaseModal;